const FEATURE_NAME = 'consent-tracking-api';
const FEATURE_VERSION = '0.1';
const CONSENT_CATEGORIES = ['preferences', 'analytics', 'marketing'];
const CONSENT_METAFIELD_KEY = 'koketa-consent-version';
const RECORDED_CONSENT_VALUES = new Set(['yes', 'no']);

class KoketaCookieConsent extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true' || this.dataset.enabled !== 'true') return;

    this.dataset.initialized = 'true';
    this.banner = this.querySelector('[data-koketa-cookie-banner]');
    this.dialog = this.querySelector('[data-koketa-cookie-preferences]');
    this.form = this.querySelector('[data-koketa-cookie-form]');
    this.status = this.querySelector('[data-koketa-cookie-status]');
    this.previewMode = new URLSearchParams(window.location.search).get('koketa_cookie_preview') === '1';
    this.api = null;
    this.lastFocusedElement = null;
    this.loadAttempts = 0;
    this.messages = {
      accepted: this.dataset.statusAccepted || 'Preferences accepted.',
      rejected: this.dataset.statusRejected || 'Optional cookies rejected.',
      saved: this.dataset.statusSaved || 'Preferences saved.',
      loadRetry: this.dataset.statusLoadRetry || 'We could not load your preferences. Reload the page to try again.',
      loadError: this.dataset.statusLoadError || 'We could not load your preferences. Please try again.',
      saveError: this.dataset.statusSaveError || 'We could not save your choice. Please try again.',
    };

    this.handleClick = this.handleClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleConsentCollected = this.handleConsentCollected.bind(this);

    this.form?.addEventListener('submit', this.handleSubmit);
    document.addEventListener('click', this.handleClick);
    document.addEventListener('visitorConsentCollected', this.handleConsentCollected);

    this.loadCustomerPrivacy();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    document.removeEventListener('click', this.handleClick);
    document.removeEventListener('visitorConsentCollected', this.handleConsentCollected);
    document.body.classList.remove('koketa-custom-consent-ready');
    this.dataset.initialized = 'false';
  }

  loadCustomerPrivacy() {
    if (!window.Shopify?.loadFeatures) {
      this.loadAttempts += 1;
      if (this.loadAttempts >= 50) {
        this.handlePrivacyApiUnavailable(this.messages.loadRetry);
        return;
      }
      window.setTimeout(() => this.loadCustomerPrivacy(), 120);
      return;
    }

    window.Shopify.loadFeatures([{ name: FEATURE_NAME, version: FEATURE_VERSION }], (error) => {
      if (error || !window.Shopify.customerPrivacy) {
        this.handlePrivacyApiUnavailable(this.messages.loadError);
        return;
      }

      this.api = window.Shopify.customerPrivacy;
      try {
        this.syncControls();
        this.applyExternalContentState();
        const showInitialBanner = this.shouldShowInitialBanner();
        if (!this.api) return;

        document.body.classList.add('koketa-custom-consent-ready');
        if (showInitialBanner) this.showBanner();
        else this.hideBanner();
      } catch (privacyError) {
        this.handlePrivacyApiUnavailable(this.messages.loadError);
      }
    });
  }

  handlePrivacyApiUnavailable(message) {
    /* Do not replace Shopify's privacy state with a per-page guess. If the API is
     * unavailable, keeping the custom banner hidden prevents it from returning on
     * every navigation and leaves Shopify's native banner available as fallback. */
    this.api = null;
    document.body.classList.remove('koketa-custom-consent-ready');
    this.hideBanner();
    this.showStatus(message);
  }

  shouldShowInitialBanner() {
    if (this.previewMode) return true;
    if (!this.api || this.hasRecordedConsent()) return false;

    try {
      return this.api.shouldShowBanner() === true;
    } catch (error) {
      this.handlePrivacyApiUnavailable(this.messages.loadError);
      return false;
    }
  }

  hasRecordedConsent() {
    if (!this.api) return false;

    try {
      const current = this.api.currentVisitorConsent?.();
      if (CONSENT_CATEGORIES.every((category) => RECORDED_CONSENT_VALUES.has(current?.[category]))) return true;
    } catch (error) {
      /* Fall through to the consent metafield, which is stored by this banner in
       * the same Shopify transaction as the visitor's category choices. */
    }

    try {
      const consentVersion = this.api.getTrackingConsentMetafield?.(CONSENT_METAFIELD_KEY);
      return consentVersion !== undefined && consentVersion !== null && consentVersion !== '';
    } catch (error) {
      return false;
    }
  }

  handleClick(event) {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target || !this.contains(target)) return;

    const close = target.closest('[data-koketa-cookie-close]');
    if (close) {
      this.closePreferences();
      return;
    }

    const action = target.closest('[data-koketa-consent-action]')?.dataset.koketaConsentAction;
    if (!action) return;

    if (action === 'accept') {
      this.saveConsent({ preferences: true, analytics: true, marketing: true }, this.messages.accepted);
    } else if (action === 'reject') {
      this.saveConsent({ preferences: false, analytics: false, marketing: false }, this.messages.rejected);
    } else if (action === 'preferences') {
      this.openPreferences(target);
    }
  }

  handleSubmit(event) {
    event.preventDefault();

    const consent = Object.fromEntries(
      CONSENT_CATEGORIES.map((category) => {
        const input = this.querySelector(`[data-koketa-consent-category="${category}"]`);
        return [category, Boolean(input?.checked)];
      })
    );

    this.saveConsent(consent, this.messages.saved);
  }

  handleConsentCollected() {
    this.syncControls();
    this.applyExternalContentState();
    if (this.hasRecordedConsent()) this.hideBanner();
  }

  saveConsent(consent, successMessage) {
    if (!this.api) {
      this.showStatus(this.messages.saveError);
      return;
    }

    this.setBusy(true);

    try {
      this.api.setTrackingConsent(
        {
          ...consent,
          metafields: [{ key: CONSENT_METAFIELD_KEY, value: this.dataset.consentVersion }],
        },
        (result) => {
          this.setBusy(false);

          if (result?.error) {
            this.showStatus(this.messages.saveError);
            return;
          }

          this.syncControls();
          this.applyExternalContentState();
          this.hideBanner();
          this.closePreferences();
          this.showStatus(successMessage);
          document.dispatchEvent(new CustomEvent('koketa:privacy-changed', { detail: consent }));
        }
      );
    } catch (error) {
      this.setBusy(false);
      this.showStatus(this.messages.saveError);
    }
  }

  syncControls() {
    if (!this.api) return;

    const current = this.api.currentVisitorConsent();
    CONSENT_CATEGORIES.forEach((category) => {
      const input = this.querySelector(`[data-koketa-consent-category="${category}"]`);
      if (input) input.checked = current?.[category] === 'yes';
    });
  }

  applyExternalContentState() {
    if (!this.api) return;

    const preferencesAllowed = this.api.preferencesProcessingAllowed();

    document.querySelectorAll('[data-koketa-consent-src="preferences"]').forEach((iframe) => {
      const container = iframe.closest('[data-koketa-external-content]');
      const placeholder = container?.querySelector('[data-koketa-external-placeholder]');

      if (preferencesAllowed) {
        if (!iframe.src && iframe.dataset.src) iframe.src = iframe.dataset.src;
        iframe.hidden = false;
        if (placeholder) placeholder.hidden = true;
      } else {
        iframe.hidden = true;
        if (iframe.src) iframe.removeAttribute('src');
        if (placeholder) placeholder.hidden = false;
      }
    });
  }

  showBanner() {
    if (!this.banner) return;
    this.banner.hidden = false;
    document.body.classList.add('koketa-cookie-banner-visible');
  }

  hideBanner() {
    if (!this.banner) return;
    this.banner.hidden = true;
    document.body.classList.remove('koketa-cookie-banner-visible');
  }

  openPreferences(trigger) {
    if (!this.dialog) return;

    this.lastFocusedElement = trigger instanceof HTMLElement ? trigger : document.activeElement;
    this.syncControls();

    if (typeof this.dialog.showModal === 'function') {
      if (!this.dialog.open) this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
  }

  closePreferences() {
    if (!this.dialog?.open) return;
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else this.dialog.removeAttribute('open');
    this.lastFocusedElement?.focus?.();
  }

  setBusy(isBusy) {
    this.querySelectorAll('button').forEach((button) => {
      button.disabled = isBusy;
    });
    this.setAttribute('aria-busy', String(isBusy));
  }

  showStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('koketa-cookie-consent')) {
  customElements.define('koketa-cookie-consent', KoketaCookieConsent);
}

/* Keep the footer/location privacy entry points reliable even if Shopify moves or
 * re-renders the static consent section while the storefront is open. */
if (!window.__koketaCookiePreferencesBound) {
  window.__koketaCookiePreferencesBound = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const opener = target?.closest?.('[data-koketa-cookie-open]');
    if (!opener) return;

    event.preventDefault();
    document.querySelector('koketa-cookie-consent')?.openPreferences?.(opener);
  });
}
